import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment-timezone';

// actions / utils
import { loadConfig } from '../../../actions/siteConfig';
import { updateEnrollment } from '../../../actions/enrollment';
import { sendEsign } from '../../../actions/esign';
import { cap, prettifyPhone } from '../../../common/utils/prettify';
import { isValid } from '../../../utils/form';
import { getTPMODisclaimerText } from '../../../common/utils/tpmo';

// component
import { TextInput } from './components';
import { LoaderButton } from '../../../common/components';

function Preview({
  greeting,
  closing,
  signature,
  planName,
  carrier,
  agentPhone,
  medicareType,
  tpmoDisclaimer,
}) {
  const [ emailBlocks, setEmailBlocks ] = useState({});

  useEffect(() => {
    loadConfig('pinpoint_esign_email_text_json', true)
      .then((data) => setEmailBlocks(data))
      .catch((err) => console.log(err));
  }, []);

  const linkClass = 'text-decoration-underline text-primary';

  return (
    <div
      style={{ wordBreak: 'break-word', borderLeft: 'solid 3px var(--bs-gray-300)' }}
      className="mb-5 ms-2 mt-2 ps-4"
    >
      <p>{emailBlocks.block_00} {medicareType} {emailBlocks.block_01}</p>
      <p><span className="bg-bgyellow">{greeting}</span></p>
      <p>{emailBlocks.block_1} {carrier}, {planName} {emailBlocks.block_2}</p>
      <p>{emailBlocks.block_3}</p>
      <p className={linkClass}>{emailBlocks.block_4}</p>
      <p>{emailBlocks.block_5}</p>
      <p>{emailBlocks.block_6} {'<activation code>'}</p>
      <p>{emailBlocks.block_7}</p>
      <p>
        <span className="bg-bggreen">{closing}</span><br />
        <span className="bg-bgblue">{signature}</span><br />
        <span>{prettifyPhone(agentPhone)}</span><br />
      </p>
      <p>{emailBlocks.material_id}</p>
      <p>{tpmoDisclaimer}</p>
      <p className={linkClass}>{emailBlocks.block_9}</p>
    </div>
  );
}

function SendShopperEmail() {
  const dispatch = useDispatch();
  const { values } = useSelector(store => store);
  const {
    first_name: shopperFirstName,
    last_name: shopperLastName,
    esignature_email_timestamps: esigEmailTimestamps = [],
    quote: { quoteID },
    email: emailFromEnrollmentForm,
    effective_date: effectiveDate,
    county: countyFips,
  } = values;
  const lastSentTimestamp = esigEmailTimestamps.at(-1);
  const {
    firstName: agentFirstName,
    lastName: agentLastName,
    mobilePhone,
    homePhone,
    carrierPermissions,
  } = useSelector(store => store.agent);
  const planYear = moment(effectiveDate).year();
  const { submittedFlag } = useSelector(store => store.enrollment);
  const agentPhone = mobilePhone || homePhone;
  const { planName, carrier, planType } = useSelector(store => store.plan);
  const { email: emailFromAgentPortal, username: shopperUsername } = useSelector(store => store.shopper);
  const { email: emailValidationRegex } = useSelector(store => store.validation);
  const medicareType = (planType === 'PDP') ? 'Prescription Drug' : 'Advantage';

  function setInitialEmailState(resetState = false) {
    if ((submittedFlag || lastSentTimestamp) && !resetState) return '';
    if (emailFromEnrollmentForm) return emailFromEnrollmentForm;
    if (emailFromAgentPortal) return emailFromAgentPortal;
    return '';
  }

  const [ loading, setLoading ] = useState(false);
  const [ email, setEmail ] = useState(setInitialEmailState());
  const [ greeting, setGreeting ] = useState(`${cap(shopperFirstName)} ${cap(shopperLastName)},`);
  const [ closing, setClosing ] = useState('Thank you,');
  const [ signature, setSignature ] = useState(`${cap(agentFirstName)} ${cap(agentLastName)}`);
  const [ allowResend, setAllowResend ] = useState(!lastSentTimestamp);
  const [ emailValid, setEmailValid ] = useState();
  const [ sendError, setSendError ] = useState();
  const [ tpmoDisclaimerText, setTpmoDisclaimerText ] = useState('');

  useEffect(() => {
    getTPMODisclaimerText({ planYear, carrierPermissions, countyFips })
      .then(text => setTpmoDisclaimerText(text));
  }, []);

  function handleSubmit() {
    setLoading(true);
    setSendError('');
    const data = {
      email,
      greeting,
      closing,
      signature,
      quote_id: quoteID,
      shopper_username: shopperUsername,
      tpmo_disclaimer: tpmoDisclaimerText,
    };
    const valid = isValid({ value: email, validationRegex: emailValidationRegex });
    setEmailValid(valid);
    if (valid) {
      sendEsign('email', data).then(() => {
        const updatedArray = [ ...esigEmailTimestamps, moment().toISOString() ];
        const updatedValues = { ...values, esignature_email_timestamps: updatedArray };
        updateEnrollment({ values: updatedValues }).then(() => {
          dispatch({ type: 'SET_FIELD_VALUE', name: 'esignature_email_timestamps', value: updatedArray });
        }).finally(() => {
          setLoading(false);
          setAllowResend(false);
        });
      }).catch(() => {
        setSendError('Error sending email');
      }).finally(() => {
        setLoading(false);
        setEmailValid(undefined);
        setEmail('');
      });
    // if email is invalid, don't actually submit
    } else {
      setSendError('Invalid email');
      setLoading(false);
    }
  }

  function handleEmailChange(e) {
    const { value: val } = e.target;
    setEmailValid(undefined);
    setEmail(val);
    setSendError('');
  }

  return (
    <>
      <div className="row">
        <TextInput
          required
          label="Email to"
          name="send-shopper-email"
          value={email}
          handleChange={(e) => handleEmailChange(e)}
          valid={emailValid}
        />
        <TextInput
          label="Greeting"
          name="send-shopper-email-greeting"
          className="bg-bgyellow"
          value={greeting}
          handleChange={(e) => setGreeting(e.target.value)}
        />
      </div>

      <div className="row mt-3">
        <TextInput
          label="Closing"
          name="send-shopper-email-closing"
          className="bg-bggreen"
          value={closing}
          handleChange={(e) => setClosing(e.target.value)}
        />
        <TextInput
          label="Signature"
          name="send-shopper-email-signature"
          className="bg-bgblue"
          value={signature}
          handleChange={(e) => setSignature(e.target.value)}
        />
      </div>

      <div className="row mt-4">
        <div className="col-md-4">
          <LoaderButton
            onClick={() => handleSubmit()}
            className="btn btn-primary fw-bold"
            text="Send email"
            loadingText="Sending"
            loading={loading}
            disabled={(lastSentTimestamp && !allowResend) || submittedFlag}
          />
          <br />
          {sendError && (
            <i className="text-danger">
              {sendError}
            </i>
          )}
        </div>

        <div className="d-flex col align-items-center justify-content-end">
          {lastSentTimestamp && (
            <>
              {!allowResend && (
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary me-4"
                  onClick={() => {
                    setAllowResend(true);
                    setEmail(setInitialEmailState(true));
                  }}
                  disabled={submittedFlag}
                >
                  Enable Resend?
                </button>
              )}
              <i className="text-info fw-bold">
                Last sent {moment(lastSentTimestamp).format('MM/DD/YYYY h:mma')}
              </i>
            </>
          )}
        </div>
      </div>

      <div className="row mt-4">
        <Preview
          greeting={greeting}
          closing={closing}
          signature={signature}
          planName={planName}
          carrier={carrier}
          agentPhone={agentPhone}
          medicareType={medicareType}
          tpmoDisclaimer={tpmoDisclaimerText}
        />
      </div>
    </>
  );
}

export default SendShopperEmail;
